import React, { useMemo, useState, useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Session, useRecruitmentInvitationQueue } from '@app/api';
import { InvitationQueueDashboard } from './InvitationQueueDashboard';
import { CenteredLoader, SideBarItem, SideMenuPage } from 'components';
import { Route, Routes } from 'react-router-dom';
import { NextInvitations } from './NextInvitations';
import { ScoringConfiguration } from './components/ScoringConfiguration';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  wrapper: {
    flex: 1,
    position: 'relative',
  },
  absoluteFill: {
    ...theme.absoluteFill,
  },
  container: {
    padding: theme.marginBase * 4,
  },
  content: {
    maxWidth: '100%',
  },
}));

interface Props {
  session: Session;
}

export const InvitationQueue = ({ session }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { data: queue, isLoading } = useRecruitmentInvitationQueue(
    session.recruitmentId,
  );

  const [scoringConfigOpened, setScoringConfigOpened] = useState(false);
  const toggleScoringConfig = useCallback(
    () => setScoringConfigOpened((val) => !val),
    [setScoringConfigOpened],
  );

  const menuItems = useMemo(
    () => [
      <SideBarItem
        text={t('sessions.details.invitationQueue.dashboard')}
        to="."
        end
      />,
      <SideBarItem
        text={t('sessions.details.invitationQueue.nexts')}
        to="nexts"
      />,
    ],
    [],
  );

  if (isLoading) {
    return <CenteredLoader />;
  }

  if (!queue) {
    return null;
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.absoluteFill}>
        <SideMenuPage
          navTitle={t('sessions.details.invitationQueue.title')}
          hideNavOnTablet
          menuItems={menuItems}
          noScroll
          extraRightMenu={
            <ScoringConfiguration
              onRequestClose={toggleScoringConfig}
              queue={queue}
            />
          }
          rightMenuOpened={scoringConfigOpened}
        >
          <Routes>
            <Route
              path="*"
              element={<InvitationQueueDashboard session={session} />}
            />
            <Route
              path="nexts"
              element={
                <NextInvitations
                  queueId={queue.id}
                  sessionId={session.id}
                  toggleConfig={toggleScoringConfig}
                />
              }
            />
          </Routes>
        </SideMenuPage>
      </div>
    </div>
  );
};
